/* istanbul ignore file */

import { Injectable } from '@angular/core';

import { DomController } from '@ionic/angular';

import { SettingsService } from './settings.service';
import { PlatformService } from './ssr/platform.service';
import { WindowService } from './ssr/window.service';

const SCROLL_ASSIST_SPEED = 0.3;
const SCROLLING_DURATION = 400;
const HORIZONTAL_SCROLLING_DURATION = 400;

@Injectable({ providedIn: 'root' })
export class ScrollService {
  constructor(
    private domCtrl: DomController,
    private windowService: WindowService,
    private settingsService: SettingsService,
    private platformService: PlatformService,
  ) {}

  // Method that scrolls horizontally the tabs container in order to show the tab that
  // has been selected if it was not visible ENTIRELY when it was selected
  public scrollHorizontallyToSelectedTab(tabElement: HTMLElement, duration?: number): Promise<void> {
    return new Promise((resolve) => {
      this.domCtrl.write(() => {
        if (!tabElement || !tabElement.parentElement) {
          resolve();
          return;
        }

        const tabsContainer = tabElement.parentElement;
        const tabsContainerWidth = tabElement.parentElement.getBoundingClientRect().width;
        const tabsContainerScrollWidth = tabsContainer.scrollWidth;
        const tabsContainerScrollPosition = tabsContainer.scrollLeft;

        const selectedTabWidth = tabElement.getBoundingClientRect().width;
        const selectedTabPosition = tabElement.getBoundingClientRect().x;
        const selectedTabDesiredPosition = (tabsContainerWidth - selectedTabWidth) / 2;

        const isCentered = selectedTabPosition === selectedTabDesiredPosition;

        if (isCentered) {
          resolve();
          return;
        }

        const delta = selectedTabPosition - selectedTabDesiredPosition;

        // We need to know how much is the max or min amount that
        // the tabs can be scrolled since it doesn't make sense that
        // the endPosition is higher than the total width of the scroll
        // or lower than 0
        let scrollStartMinValue: number;
        let scrollEndMaxValue: number;

        const shouldInvertStartAndEnd = this.settingsService.getLanguage().rtl;

        if (!shouldInvertStartAndEnd) {
          scrollStartMinValue = 0;
          scrollEndMaxValue = tabsContainerScrollWidth - tabsContainerWidth;
        } else {
          scrollStartMinValue = -1 * (tabsContainerScrollWidth - tabsContainerWidth);
          scrollEndMaxValue = 0;
        }

        const startingScrollLeft = tabsContainerScrollPosition;
        const endingScrollLeft =
          delta > 0
            ? Math.min(scrollEndMaxValue, startingScrollLeft + Math.abs(delta))
            : Math.max(scrollStartMinValue, startingScrollLeft - Math.abs(delta));

        // If we don't need to do anything, still wait for the scrolling time
        // because otherwise changing some tabs would not require the same time
        // as changing some other tabs
        if (startingScrollLeft === endingScrollLeft) {
          void this.platformService.wait(HORIZONTAL_SCROLLING_DURATION).then(() => resolve());
          return;
        }

        if (duration === 0) {
          tabsContainer.scrollLeft = endingScrollLeft;
          resolve();
          return;
        }

        this.animateHorizontalScroll(tabsContainer, startingScrollLeft, endingScrollLeft, HORIZONTAL_SCROLLING_DURATION, resolve);
      });
    });
  }

  // Code stolen from Ionic source code to mimic the speed of Ionic's scrolling methods
  // https://github.com/ionic-team/ionic-v3/blob/master/src/components/input/input.ts#L805
  public getScrollingDuration(distance: number): number {
    return Math.min(SCROLLING_DURATION, Math.max(150, distance / SCROLL_ASSIST_SPEED));
  }

  private animateHorizontalScroll(
    element: { scrollLeft: number },
    start: number,
    end: number,
    duration: number,
    resolveFn: () => void,
  ): void {
    const delta = end - start;

    let startTime: number;
    if (this.windowService.isWindowDefined && this.windowService.window.performance?.now) {
      startTime = this.windowService.window.performance.now();
    } else if (Date.now) {
      startTime = Date.now();
    } else {
      startTime = new Date().getTime();
    }

    const easing = (t: number, b: number, c: number, d: number) => c * ((t = t / d - 1) * t * t + 1) + b;

    const loop = (time?: number) => {
      const t = !time ? 0 : time - startTime;
      const factor = easing(t, 0, 1, duration);

      element.scrollLeft = start + delta * factor;

      if (t < duration && element.scrollLeft !== end) {
        requestAnimationFrame(loop);
      } else {
        resolveFn();
      }
    };

    loop();
  }
}
